import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ServerImage from '../components/ServerImage';
import { FaChevronDown, FaArrowRight } from 'react-icons/fa';
import axios from 'axios';
import { SERVER } from '../config/config';

const Services = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedReviews, setExpandedReviews] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'website', label: 'Website creation' },
    { id: 'mobile', label: 'Mobile apps' },
    { id: 'branding', label: 'Branding' },
    { id: 'printing', label: 'Printing' },
    { id: 'graphic', label: 'Graphic design' }
  ];

  useEffect(() => {
    // Scroll to top when component mounts or location changes
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${SERVER}/services`);
      setServices(response.data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleReviews = (serviceId) => {
    setExpandedReviews(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center h-64 pt-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-6 pb-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="mb-8 md:mb-16">
            <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
              Our services
            </h1>
            
            {/* Category Pills */}
            <div className="flex flex-wrap gap-3">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className="px-4 md:px-4 py-1 rounded-full text-sm md:text-base font-medium transition-all duration-300 bg-white text-gray-700 border border-black hover:border-gray-400"
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>

          {/* Services List */}
          <div className="space-y-2 md:space-y-24">
            {services.map((service, index) => {
              // Collect all service images
              const serviceImages = [
                service.image,
                service.image_2,
                service.image_3
              ].filter(Boolean);
              
              // Determine layout based on order_index
              // Even order_index (0, 2, 4...) = content on left, image on right
              // Odd order_index (1, 3, 5...) = content on right, image on left
              const isEven = service.order_index % 2 === 0;
              
              return (
                <div key={service.id} className={`flex flex-col gap-2 md:gap-12 items-center ${
                  isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}>
                  {/* Content - Always first on mobile, positioned based on order_index on desktop */}
                  <div className="space-y-2 md:space-y-4 order-2 lg:order-none lg:flex-1">
                    {/* Service Tags */}
                    <div className="flex flex-wrap gap-1 md:gap-4">
                      {service.tags && service.tags.map((tag, tagIndex) => (
                        <div key={tagIndex} className="flex items-center px-2 py-1 bg-gray-50 rounded-full gap-1 mb-4">
                          <div 
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: 'var(--primary-blue)' }}
                          ></div>
                          <span className="text-sm text-gray-400">{tag}</span>
                        </div>
                      ))}
                    </div>

                    {/* Service Title and Button */}
                    <div className="flex items-center justify-between">
                      <h2 className="text-2xl lg:text-4xl font-bold text-gray-900">
                        {service.title}
                      </h2>
                      <button 
                        onClick={() => navigate(`/portfolio?category=${encodeURIComponent(service.title)}`)}
                        className="flex max-md:hidden items-center gap-2 text-white px-6 py-2 rounded-full font-semibold hover:opacity-90 transition-all duration-300"
                        style={{ 
                          backgroundColor: 'var(--primary-blue)',
                          boxShadow: '0 4px 12px rgba(29, 68, 233, 0.4)'
                        }}
                      >
                        View portfolio
                        <FaArrowRight className="text-sm -rotate-45" />
                      </button>
                    </div>

                    {/* Service Description */}
                    <p className="text-sm md:text-lg text-gray-600">
                      {service.description}
                    </p>
                    <div className="flex justify-end">
                      <button 
                        onClick={() => navigate(`/portfolio?category=${encodeURIComponent(service.title)}`)}
                        className="flex items-center text-sm md:hidden gap-2 text-white px-6 py-2 rounded-full font-semibold hover:opacity-90 transition-all duration-300 mb-2"
                        style={{ 
                          backgroundColor: 'var(--primary-blue)',
                          boxShadow: '0 4px 12px rgba(29, 68, 233, 0.4)'
                        }}
                      >
                        View portfolio
                        <FaArrowRight className="text-sm -rotate-45" strokeWidth="0.3"  />
                      </button>
                    </div>

                    {/* Reviews Section */}
                    {service.reviews && service.reviews.length > 0 && (
                      <div className="space-y-4">
                        <button
                          onClick={() => toggleReviews(service.id)}
                          className="flex items-center justify-between gap-2 py-2 px-4 rounded-full text-gray-900 font-medium w-full bg-gray-50"
                        >
                          <div className="flex items-center gap-2 text-gray-400">
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: 'var(--primary-blue)' }}
                            ></div>
                            {service.reviews.length} Reviews
                          </div>
                          <FaChevronDown 
                            className={`text-sm transition-transform duration-300 ${
                              expandedReviews[service.id] ? 'rotate-180' : ''
                            }`} 
                          />
                        </button>

                        {expandedReviews[service.id] && (
                          <div className="rounded-lg p-6">
                            <div className="max-h-24 overflow-y-auto space-y-4">
                              {service.reviews.map((review, reviewIndex) => (
                                <div key={reviewIndex} className="space-y-2">
                                  <h4 className="font-semibold text-gray-900">{review.reviewer_name}</h4>
                                  <p className="text-[#8F8F8F]">&quot;{review.review_text}&quot;</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Images - Second on mobile, positioned based on order_index on desktop */}
                  <div className="relative order-1 lg:order-none lg:flex-1 w-full">
                    <div className="flex justify-center">
                      {serviceImages.length === 1 ? (
                        <div className="w-full md:w-[90%] lg:h-96 rounded-2xl overflow-hidden">
                          <ServerImage
                            src={serviceImages[0]}
                            alt={service.title}
                            fallback="/services/website.png"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : serviceImages.length === 2 ? (
                        <div className="w-full md:w-[90%] lg:h-96 flex gap-2">
                          {serviceImages.map((img, imgIdx) => (
                            <div 
                              key={imgIdx} 
                              className="flex-1 rounded-2xl overflow-hidden"
                              style={{ minWidth: 0 }}
                            >
                              <ServerImage
                                src={img}
                                alt={`${service.title} ${imgIdx + 1}`}
                                fallback="/services/website.png"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="w-full md:w-[90%] lg:h-96 flex gap-2">
                          {serviceImages.map((img, imgIdx) => (
                            <div 
                              key={imgIdx} 
                              className="flex-1 rounded-2xl overflow-hidden"
                              style={{ minWidth: 0 }}
                            >
                              <ServerImage
                                src={img}
                                alt={`${service.title} ${imgIdx + 1}`}
                                fallback="/services/website.png"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Empty State */}
          {services.length === 0 && (
            <div className="text-center py-2 md:py-16">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaArrowRight className="text-3xl text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No Services Available</h3>
              <p className="text-gray-600">
                Check back soon for our latest service offerings.
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Services;
